import { useState, useEffect } from "react";
import { supabase } from "../../supabase";
import ConfirmModal from "../ConfirmModal";

const STATUS_COLORS = {
  "Brief":            { bg: "#C9A96E18", border: "#C9A96E40", text: "#C9A96E" },
  "Brief & Vision":   { bg: "#C9A96E18", border: "#C9A96E40", text: "#C9A96E" },
  "Plans & Permis":   { bg: "#7EB8C918", border: "#7EB8C940", text: "#7EB8C9" },
  "Travaux":          { bg: "#7EB8C918", border: "#7EB8C940", text: "#7EB8C9" },
  "En cours":         { bg: "#7EB8C918", border: "#7EB8C940", text: "#7EB8C9" },
  "Livraison":        { bg: "#A8C97E18", border: "#A8C97E40", text: "#A8C97E" },
  "Terminé":          { bg: "#A8C97E18", border: "#A8C97E40", text: "#A8C97E" },
};

function statusColor(s) {
  return STATUS_COLORS[s] || { bg: "#25242018", border: "#2A2924", text: "#7A7468" };
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [owners, setOwners] = useState({});
  const [memberCounts, setMemberCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  useEffect(() => {
    async function fetchData() {
      const { data: projs } = await supabase
        .from("projects")
        .select("id, name, address, client_name, status, progress, deadline, owner_id, created_at")
        .order("created_at", { ascending: false });

      if (!projs || projs.length === 0) { setLoading(false); return; }

      const ownerIds = [...new Set(projs.map(p => p.owner_id).filter(Boolean))];
      const projIds = projs.map(p => p.id);

      const [ownersRes, membersRes] = await Promise.all([
        ownerIds.length
          ? supabase.from("profiles").select("id, name").in("id", ownerIds)
          : { data: [] },
        supabase.from("project_members").select("project_id").in("project_id", projIds),
      ]);

      const ownerMap = {};
      (ownersRes.data || []).forEach(o => { ownerMap[o.id] = o.name; });

      const mcMap = {};
      (membersRes.data || []).forEach(m => { mcMap[m.project_id] = (mcMap[m.project_id] || 0) + 1; });

      setProjects(projs);
      setOwners(ownerMap);
      setMemberCounts(mcMap);
      setLoading(false);
    }
    fetchData();
  }, []);

  const confirmDelete = async () => {
    const { error } = await supabase.from("projects").delete().eq("id", deleteTarget.id);
    if (error) { showToast("❌ " + error.message); setDeleteTarget(null); return; }
    setProjects(prev => prev.filter(p => p.id !== deleteTarget.id));
    setDeleteTarget(null);
    showToast("✓ Projet supprimé");
  };

  const allStatuses = [...new Set(projects.map(p => p.status).filter(Boolean))];
  const visible = filterStatus === "all" ? projects : projects.filter(p => p.status === filterStatus);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400 }}>Projets</h1>
        <p style={{ fontSize: 13, color: "#7A7468", marginTop: 4 }}>
          {loading ? "Chargement…" : `${projects.length} projet${projects.length !== 1 ? "s" : ""}`}
        </p>
      </div>

      {/* Status filter chips */}
      {!loading && allStatuses.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
          {["all", ...allStatuses].map(s => {
            const active = filterStatus === s;
            const count = s === "all" ? projects.length : projects.filter(p => p.status === s).length;
            return (
              <button
                key={s}
                className="btn"
                onClick={() => setFilterStatus(s)}
                style={{ padding: "5px 12px", borderRadius: 99, fontSize: 11, fontWeight: active ? 600 : 400, border: `1px solid ${active ? "#C9A96E60" : "#2A2924"}`, background: active ? "#C9A96E18" : "transparent", color: active ? "#C9A96E" : "#7A7468" }}
              >
                {s === "all" ? "Tous" : s}
                <span style={{ marginLeft: 5, opacity: .6 }}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ background: "#1A1916", border: "1px solid #2A2924", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #2A2924" }}>
              {["Nom", "Architecte", "Client", "Statut", "Avancement", "Deadline", "Membres", "Créé le", ""].map((h, i) => (
                <th key={i} style={{ padding: "12px 16px", textAlign: i === 6 ? "center" : "left", fontSize: 10, color: "#7A7468", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: 48, textAlign: "center" }}>
                <div className="spinner" style={{ margin: "0 auto" }} />
              </td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 48, textAlign: "center", color: "#7A7468", fontSize: 13 }}>
                Aucun projet
              </td></tr>
            ) : visible.map((proj, i) => {
              const sc = statusColor(proj.status);
              const progress = proj.progress || 0;
              return (
                <tr key={proj.id} style={{ borderBottom: i < visible.length - 1 ? "1px solid #1F1E1A" : "none" }}>
                  {/* Nom */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#E8E2D8", marginBottom: proj.address ? 2 : 0 }}>{proj.name}</div>
                    {proj.address && <div style={{ fontSize: 11, color: "#5A5450" }}>{proj.address}</div>}
                  </td>
                  {/* Architecte */}
                  <td style={{ padding: "13px 16px", fontSize: 13, color: "#7A7468" }}>{owners[proj.owner_id] || "—"}</td>
                  {/* Client */}
                  <td style={{ padding: "13px 16px", fontSize: 13, color: "#7A7468" }}>{proj.client_name || "—"}</td>
                  {/* Statut */}
                  <td style={{ padding: "13px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 4, background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text }}>
                      {{ EN_COURS: "En cours", TERMINE: "Terminé", EN_ATTENTE: "En attente", PLANIFIE: "Planifié", SUSPENDU: "Suspendu" }[proj.status] || proj.status || "—"}
                    </span>
                  </td>
                  {/* Avancement */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 90 }}>
                      <div style={{ flex: 1, height: 4, background: "#252420", borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${progress}%`, background: "#C9A96E", borderRadius: 99, transition: "width .4s" }} />
                      </div>
                      <span style={{ fontSize: 11, color: "#7A7468", flexShrink: 0, minWidth: 28, textAlign: "right" }}>{progress}%</span>
                    </div>
                  </td>
                  {/* Deadline */}
                  <td style={{ padding: "13px 16px", fontSize: 12, color: "#7A7468", whiteSpace: "nowrap" }}>{fmtDate(proj.deadline)}</td>
                  {/* Membres */}
                  <td style={{ padding: "13px 16px", textAlign: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: memberCounts[proj.id] > 0 ? "#E8E2D8" : "#7A7468" }}>
                      {memberCounts[proj.id] || 0}
                    </span>
                  </td>
                  {/* Créé le */}
                  <td style={{ padding: "13px 16px", fontSize: 12, color: "#7A7468", whiteSpace: "nowrap" }}>{fmtDate(proj.created_at)}</td>
                  {/* Actions */}
                  <td style={{ padding: "13px 16px" }}>
                    <button
                      className="btn"
                      onClick={() => setDeleteTarget(proj)}
                      style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #FC818140", fontSize: 11, color: "#FC8181", background: "#FC818112", fontWeight: 500 }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Supprimer ce projet ?"
          message={`Le projet "${deleteTarget.name}" et toutes ses données seront définitivement supprimés.`}
          confirmLabel="Supprimer"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
